import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Button from '../../../../../shared/components/general/Button';
import PageError from '../../../../../shared/components/general/PageError';
import SettingsCard from '../../../../../shared/components/structure/SettingsCard';
import ContentCollapsingSection from '../../../../../shared/components/structure/ContentCollapsingSection';
import ManagedWorkQueuePanel from '../../../../../shared/scenes/organizationSettings/components/ManagedWorkQueuePanel';
import { getFoodOrderingModuleId, getHealthcareSchedulingModuleId } from '../../../../../shared/constants/ModuleConsts';
import { PlatformOrganizationItemModel, PlatformSchedulingApplicationItemModel } from './PlatformOrganizationItem';
import {
    PlatformSchedulingOrganizationQueueResult,
    queuePlatformSchedulingSyncAndPublish
} from '../platformSchedulingManagement';
import { getSelectableApplications, getVisibleManagementOrganizations } from '../platformManagementSelection';

export interface PlatformSchedulingManagementProps {
    organizations: PlatformOrganizationItemModel[];
    onProcessingChange: (isProcessing: boolean) => void;
    onReload?: () => void;
    vertical?: 'ordering' | 'scheduling';
    discoveryProgress?: {
        loadedCount: number;
        totalCount: number;
        isComplete: boolean;
        hasErrors: boolean;
    };
}

export const getDefaultSelectedApplicationIds = (organizations: PlatformOrganizationItemModel[]): Set<string> =>
    new Set(organizations.reduce((ids, organization) => [
        ...ids,
        ...organization.matchingApps.filter(application => application.isLive).map(application => application.id)
    ], [] as string[]));

const PlatformSchedulingManagement: React.FC<PlatformSchedulingManagementProps> = ({
    organizations,
    onProcessingChange,
    onReload,
    vertical = 'scheduling',
    discoveryProgress = { loadedCount: 0, totalCount: 0, isComplete: true, hasErrors: false }
}) => {
    const [selectedApplicationIds, setSelectedApplicationIds] = useState<Set<string>>(
        () => getDefaultSelectedApplicationIds(organizations)
    );
    const [collapsedOrganizationIds, setCollapsedOrganizationIds] = useState<Set<string>>(new Set());
    const [isQueueManagementCollapsed, setIsQueueManagementCollapsed] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState<PlatformSchedulingOrganizationQueueResult[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [successes, setSuccesses] = useState<string[]>([]);
    const seenApplicationIds = useRef<Set<string>>(new Set());
    const shouldSelectNewLiveApplications = useRef(true);
    const isOrdering = vertical === 'ordering';
    const verticalLabel = isOrdering ? 'Ordering' : 'Scheduling';
    const appTypeLabel = isOrdering ? 'ordering' : 'scheduling';
    const moduleName = isOrdering ? 'Food Ordering' : 'Healthcare Scheduling';
    const coreModuleId = isOrdering ? getFoodOrderingModuleId() : getHealthcareSchedulingModuleId();
    const isDiscoveryReady = discoveryProgress.isComplete && !discoveryProgress.hasErrors;

    useEffect(() => {
        setSelectedApplicationIds(previous => {
            const next = new Set(previous);
            organizations.forEach(organization => organization.matchingApps.forEach(application => {
                if (!seenApplicationIds.current.has(application.id)) {
                    seenApplicationIds.current.add(application.id);
                    if (application.isLive && shouldSelectNewLiveApplications.current) {
                        next.add(application.id);
                    }
                }
            }));
            return next;
        });
    }, [organizations]);

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const visibleOrganizations = useMemo(
        () => getVisibleManagementOrganizations(organizations, normalizedQuery, selectedApplicationIds),
        [organizations, normalizedQuery, selectedApplicationIds]
    );

    const targets = useMemo(() => organizations.map(organization => ({
        id: organization.id,
        name: organization.name,
        applicationIds: getSelectableApplications(organization)
            .filter(application => selectedApplicationIds.has(application.id))
            .map(application => application.id),
        sharedConfigurationCandidates: organization.sharedConfigurationCandidates.map(candidate => ({
            id: candidate.id,
            name: candidate.name
        }))
    })).filter(target => target.applicationIds.length > 0), [organizations, selectedApplicationIds]);

    const selectedApplicationCount = targets.reduce((total, target) => total + target.applicationIds.length, 0);
    const selectedDraftCount = organizations.reduce((total, organization) => total + getSelectableApplications(organization).filter(
        application => !application.isLive && selectedApplicationIds.has(application.id)
    ).length, 0);

    const toggleApplication = (applicationId: string) => {
        setSelectedApplicationIds(previous => {
            const next = new Set(previous);
            if (next.has(applicationId)) next.delete(applicationId);
            else next.add(applicationId);
            return next;
        });
    };

    const toggleApplications = (applications: PlatformSchedulingApplicationItemModel[]) => {
        setSelectedApplicationIds(previous => {
            const next = new Set(previous);
            const shouldSelect = applications.some(application => !next.has(application.id));
            applications.forEach(application => {
                if (shouldSelect) next.add(application.id);
                else next.delete(application.id);
            });
            return next;
        });
    };

    const selectAllLive = () => setSelectedApplicationIds(previous => {
        shouldSelectNewLiveApplications.current = true;
        const next = new Set(previous);
        organizations.forEach(organization => organization.matchingApps.forEach(application => {
            if (application.isLive) next.add(application.id);
        }));
        return next;
    });

    const toggleOrganizationCollapsed = (organizationId: string) => {
        setCollapsedOrganizationIds(previous => {
            const next = new Set(previous);
            if (next.has(organizationId)) next.delete(organizationId);
            else next.add(organizationId);
            return next;
        });
    };

    const handleSyncAndPublish = async () => {
        if (!isDiscoveryReady || targets.length === 0 || isProcessing) return;
        const draftWarning = selectedDraftCount > 0
            ? ` ${selectedDraftCount} selected app${selectedDraftCount === 1 ? ' is' : 's are'} not currently live and will be included.`
            : '';
        const confirmed = window.confirm(
            `Queue sync and publish for ${selectedApplicationCount} ${appTypeLabel} app${selectedApplicationCount === 1 ? '' : 's'} across ${targets.length} organization${targets.length === 1 ? '' : 's'}?${draftWarning}`
        );
        if (!confirmed) return;

        setErrors([]);
        setSuccesses([]);
        setResults([]);
        setIsProcessing(true);
        onProcessingChange(true);

        try {
            const queueResults = await queuePlatformSchedulingSyncAndPublish(targets, result => {
                setResults(previous => [...previous, result]);
            }, coreModuleId, moduleName);
            const failedOrganizations = queueResults.filter(result => result.errors.length > 0);
            const acceptedJobs = queueResults.reduce((total, result) => total + result.acceptedJobCount, 0);

            if (failedOrganizations.length > 0) {
                setErrors(failedOrganizations.map(result => `${result.organizationName}: ${result.errors.join(', ')}`));
            }
            if (acceptedJobs > 0) {
                setSuccesses([`${acceptedJobs} managed queue job${acceptedJobs === 1 ? '' : 's'} accepted. You can safely leave this page while they run.`]);
            }
        } finally {
            setIsProcessing(false);
            onProcessingChange(false);
        }
    };

    return (
        <ManagementWrapper>
            <QueueManagementSection>
                <ContentCollapsingSection
                    title="Sync & Publish Queue"
                    isCollapsedByDefault={true}
                    CardPadding={16}
                    onToggle={setIsQueueManagementCollapsed}
                >
                    {!isQueueManagementCollapsed && (
                        <ManagedWorkQueuePanel
                            organizationId=""
                            organizations={organizations.map(organization => ({
                                id: organization.id,
                                name: organization.name
                            }))}
                        />
                    )}
                </ContentCollapsingSection>
            </QueueManagementSection>

            {!isDiscoveryReady && (
                <DiscoveryStatus role="status" aria-live="polite" $hasErrors={discoveryProgress.hasErrors}>
                    <DiscoveryStatusRow>
                        <strong>Loaded {discoveryProgress.loadedCount} of {discoveryProgress.totalCount} organizations</strong>
                        <span>{discoveryProgress.hasErrors
                            ? 'Some organizations could not be loaded. Retry discovery before syncing.'
                            : 'Sync & Publish will unlock when discovery is complete.'}</span>
                    </DiscoveryStatusRow>
                    <DiscoveryTrack>
                        <DiscoveryBar $percent={discoveryProgress.totalCount > 0
                            ? (discoveryProgress.loadedCount / discoveryProgress.totalCount) * 100
                            : 0}
                        />
                    </DiscoveryTrack>
                    {discoveryProgress.hasErrors && onReload && (
                        <DiscoveryRetry type="button" onClick={onReload}>Retry discovery</DiscoveryRetry>
                    )}
                </DiscoveryStatus>
            )}

            <SettingsCard
                title={`Platform ${verticalLabel} Management`}
                subtitle={`Choose the ${appTypeLabel} apps to sync and publish. Live apps are selected by default; search by app name to find and add a draft app.`}
            >
                <TopBar>
                    <SummaryGrid>
                        <SummaryItem><strong>{targets.length}</strong><span>Organizations</span></SummaryItem>
                        <SummaryItem><strong>{selectedApplicationCount}</strong><span>Selected apps</span></SummaryItem>
                        <SummaryItem $warning={selectedDraftCount > 0}><strong>{selectedDraftCount}</strong><span>Not live</span></SummaryItem>
                    </SummaryGrid>
                    <ActionRow>
                        <TextButton type="button" onClick={selectAllLive} disabled={isProcessing}>Select all live</TextButton>
                        <TextButton
                            type="button"
                            onClick={() => {
                                shouldSelectNewLiveApplications.current = false;
                                setSelectedApplicationIds(new Set());
                            }}
                            disabled={isProcessing}
                        >Clear selection</TextButton>
                        <Button
                            themes={['primary']}
                            text={!isDiscoveryReady
                                ? 'Loading organizations...'
                                : isProcessing
                                    ? 'Queueing...'
                                    : `Sync & Publish ${selectedApplicationCount} App${selectedApplicationCount === 1 ? '' : 's'}`}
                            onClick={handleSyncAndPublish}
                            disabled={!isDiscoveryReady || isProcessing || selectedApplicationCount === 0}
                            loading={isProcessing}
                        />
                    </ActionRow>
                </TopBar>

                <SearchLabel htmlFor={`platform-${vertical}-app-search`}>Find an app</SearchLabel>
                <SearchInputWrap>
                    <SearchGlyph />
                    <SearchInput
                        id={`platform-${vertical}-app-search`}
                        type="text"
                        placeholder={`Search ${appTypeLabel} apps by name...`}
                        value={searchQuery}
                        onChange={event => setSearchQuery(event.target.value)}
                        disabled={isProcessing}
                    />
                    {searchQuery && <ClearButton type="button" onClick={() => setSearchQuery('')} aria-label="Clear app search">×</ClearButton>}
                </SearchInputWrap>
                {normalizedQuery && <SearchHint>Search includes live and not-live {appTypeLabel} apps.</SearchHint>}

                <OrganizationList>
                    {visibleOrganizations.map(({ organization, applications }) => {
                        const selectedCount = applications.filter(application => selectedApplicationIds.has(application.id)).length;
                        const isCollapsed = !normalizedQuery && collapsedOrganizationIds.has(organization.id);
                        return (
                            <OrganizationGroup key={organization.id}>
                                <OrganizationHeader>
                                    <OrganizationCollapseButton
                                        type="button"
                                        aria-expanded={!isCollapsed}
                                        onClick={() => toggleOrganizationCollapsed(organization.id)}
                                        disabled={isProcessing}
                                    >
                                        <OrganizationIdentity>
                                            <OrganizationTitleRow>
                                                <CollapseChevron $collapsed={isCollapsed}>⌄</CollapseChevron>
                                                <OrganizationName>{organization.name}</OrganizationName>
                                            </OrganizationTitleRow>
                                            <OrganizationMeta>{selectedCount} of {applications.length} shown apps selected</OrganizationMeta>
                                        </OrganizationIdentity>
                                    </OrganizationCollapseButton>
                                    <GroupToggle
                                        type="button"
                                        onClick={() => toggleApplications(applications)}
                                        disabled={isProcessing}
                                    >
                                        {selectedCount === applications.length ? 'Deselect shown' : 'Select shown'}
                                    </GroupToggle>
                                </OrganizationHeader>
                                {!isCollapsed && <ApplicationList>
                                    {applications.map(application => (
                                        <ApplicationRow
                                            key={application.id}
                                            as="label"
                                            $selected={selectedApplicationIds.has(application.id)}
                                            $disabled={isProcessing}
                                        >
                                            <AppCheckbox
                                                type="checkbox"
                                                checked={selectedApplicationIds.has(application.id)}
                                                onChange={() => toggleApplication(application.id)}
                                                disabled={isProcessing}
                                            />
                                            <ApplicationName>{application.name}</ApplicationName>
                                            <StatusBadge $live={application.isLive}>{application.isLive ? 'Live' : 'Not live'}</StatusBadge>
                                        </ApplicationRow>
                                    ))}
                                </ApplicationList>}
                            </OrganizationGroup>
                        );
                    })}
                    {visibleOrganizations.length === 0 && (
                        <EmptySearch>No {appTypeLabel} apps match “{searchQuery}”.</EmptySearch>
                    )}
                </OrganizationList>

                {results.length > 0 && (
                    <ResultList>
                        {results.map(result => (
                            <ResultRow key={result.organizationId} $hasErrors={result.errors.length > 0}>
                                <span>{result.organizationName}</span>
                                <span>{result.errors.length > 0
                                    ? (result.acceptedJobCount > 0 ? 'Queued with warnings' : 'Failed preflight')
                                    : `${result.acceptedJobCount} jobs queued`}</span>
                            </ResultRow>
                        ))}
                    </ResultList>
                )}
            </SettingsCard>

            {errors.length > 0 && <PageError errors={errors} onClear={() => setErrors([])} type="error" />}
            {successes.length > 0 && <PageError errors={successes} onClear={() => setSuccesses([])} type="success" />}
        </ManagementWrapper>
    );
};

const ManagementWrapper = styled.div`
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
    padding: 24px;
    overflow: auto;
`;

const QueueManagementSection = styled.div`
    margin-bottom: 18px;

    > div {
        overflow: hidden;
        border: 1px solid #dfe4ea;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 6px 20px rgba(50, 70, 97, 0.06);
    }

    .card-title-container {
        padding-left: 16px !important;
    }

    .card-body {
        padding: 16px !important;
        background: #f8fafc;

        > section {
            margin-bottom: 0;
        }
    }
`;

const DiscoveryStatus = styled.div<{ $hasErrors: boolean }>`
    margin-bottom: 18px;
    padding: 14px 16px;
    border: 1px solid ${props => props.$hasErrors ? '#edc078' : '#b9d8eb'};
    border-radius: 10px;
    background: ${props => props.$hasErrors ? '#fff8ec' : '#f1f8fc'};
`;

const DiscoveryStatusRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    color: #324661;
    font-size: 12px;

    strong { color: #1c6697; font-size: 13px; }
`;

const DiscoveryTrack = styled.div`
    height: 6px;
    margin-top: 10px;
    overflow: hidden;
    border-radius: 999px;
    background: #dceaf3;
`;

const DiscoveryRetry = styled.button`
    margin-top: 10px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #1c6697;
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
`;

const DiscoveryBar = styled.div<{ $percent: number }>`
    width: ${props => Math.max(0, Math.min(100, props.$percent))}%;
    height: 100%;
    border-radius: inherit;
    background: #1c6697;
    transition: width 180ms ease;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(120px, 170px));
    gap: 12px;
`;

const SummaryItem = styled.div<{ $warning?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 14px 16px;
    border: 1px solid ${props => props.$warning ? '#edc078' : '#dfe4ea'};
    border-radius: 10px;
    background: ${props => props.$warning ? '#fff8ec' : '#f8fafc'};

    strong { font-size: 24px; color: ${props => props.$warning ? '#9a5b00' : '#1c6697'}; }
    span { margin-top: 2px; color: #52657a; font-size: 12px; }
`;

const ActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
`;

const TextButton = styled.button`
    appearance: none;
    border: 0;
    background: transparent;
    color: #1c6697;
    padding: 8px 4px;
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const SearchLabel = styled.label`
    display: block;
    margin: 24px 0 8px;
    color: #324661;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const SearchInputWrap = styled.div`
    position: relative;
    width: 100%;
`;

const SearchGlyph = styled.span`
    position: absolute;
    left: 18px;
    top: 50%;
    width: 15px;
    height: 15px;
    border: 2px solid #75869a;
    border-radius: 50%;
    transform: translateY(-58%);
    pointer-events: none;

    &::after {
        content: '';
        position: absolute;
        width: 7px;
        height: 2px;
        right: -6px;
        bottom: -3px;
        background: #75869a;
        transform: rotate(45deg);
        border-radius: 2px;
    }
`;

const SearchInput = styled.input`
    width: 100%;
    height: 52px;
    padding: 0 48px 0 50px;
    border: 1px solid #cbd3dc;
    border-radius: 10px;
    background: #fff;
    color: #21344d;
    font-family: Muli, sans-serif;
    font-size: 14px;
    outline: none;

    &:focus { border-color: #1c6697; box-shadow: 0 0 0 3px rgba(28, 102, 151, 0.12); }
    &:disabled { background: #f3f5f7; cursor: not-allowed; }
`;

const ClearButton = styled.button`
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    appearance: none;
    border: 0;
    background: transparent;
    color: #52657a;
    font-size: 22px;
    cursor: pointer;
`;

const SearchHint = styled.div`
    margin-top: 7px;
    color: #6b7d90;
    font-size: 12px;
`;

const OrganizationList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 18px;
`;

const OrganizationGroup = styled.section`
    overflow: hidden;
    border: 1px solid #dfe4ea;
    border-radius: 12px;
    background: #fff;
`;

const OrganizationHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px;
    background: #f4f7fa;
    border-bottom: 1px solid #dfe4ea;
`;

const OrganizationCollapseButton = styled.button`
    display: flex;
    min-width: 0;
    flex: 1;
    padding: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid #1c6697;
        outline-offset: 4px;
    }

    &:disabled { cursor: not-allowed; opacity: 0.65; }
`;

const OrganizationIdentity = styled.div`
    min-width: 0;
`;

const OrganizationName = styled.div`
    overflow: hidden;
    color: #21344d;
    font-size: 14px;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const OrganizationTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
`;

const CollapseChevron = styled.span<{ $collapsed: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: #52657a;
    font-size: 18px;
    font-weight: 900;
    transform: rotate(${props => props.$collapsed ? '-90deg' : '0deg'});
    transition: transform 150ms ease;
`;

const OrganizationMeta = styled.div`
    margin-top: 2px;
    color: #6b7d90;
    font-size: 11px;
`;

const GroupToggle = styled(TextButton)`
    flex-shrink: 0;
`;

const ApplicationList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
`;

const ApplicationRow = styled.div<{ $selected: boolean; $disabled: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    padding: 13px 18px;
    border-right: 1px solid #edf0f4;
    border-bottom: 1px solid #edf0f4;
    background: ${props => props.$selected ? '#f4f9fc' : '#fff'};
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    transition: background 0.15s ease;

    &:hover { background: ${props => props.$disabled ? undefined : '#eef6fb'}; }
`;

const AppCheckbox = styled.input`
    width: 17px;
    height: 17px;
    flex-shrink: 0;
    accent-color: #1c6697;
`;

const ApplicationName = styled.span`
    min-width: 0;
    flex: 1;
    overflow: hidden;
    color: #324661;
    font-size: 13px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const StatusBadge = styled.span<{ $live: boolean }>`
    flex-shrink: 0;
    padding: 4px 8px;
    border-radius: 999px;
    background: ${props => props.$live ? '#e5f6ea' : '#eef1f4'};
    color: ${props => props.$live ? '#29743b' : '#6b7d90'};
    font-size: 10px;
    font-weight: 800;
`;

const EmptySearch = styled.div`
    padding: 32px;
    border: 1px dashed #cbd3dc;
    border-radius: 10px;
    color: #6b7d90;
    text-align: center;
`;

const ResultList = styled.div`
    margin-top: 20px;
    border-top: 1px solid #dfe4ea;
`;

const ResultRow = styled.div<{ $hasErrors: boolean }>`
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #edf0f4;
    color: ${props => props.$hasErrors ? '#a44a1f' : '#29743b'};
    font-size: 13px;
`;

export default PlatformSchedulingManagement;
